import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useKegiatanList } from '@/hooks/useKegiatan'
import { useAuthStore } from '@/stores/authStore'
import EmptyState from '@/components/ui/EmptyState'
import type { KegiatanStatusFilter } from '@/stores/kegiatanStore'
import type { Kegiatan } from '@/types/pocketbase'

// Status types
type StatusType = 'akan' | 'sedang' | 'selesai'

// Status tabs configuration
const statusTabs: { label: string; value: StatusType; color: string }[] = [
  { label: 'Akan\nBerlangsung', value: 'akan', color: '#D97706' },
  { label: 'Sedang\nBerlangsung', value: 'sedang', color: '#059669' },
  { label: 'Selesai', value: 'selesai', color: '#6B7280' },
]

// Dropdown options
const tanggalOptions = [
  { value: '', label: 'Semua Tanggal' },
  { value: '2026-10', label: 'Oktober 2026' },
  { value: '2026-11', label: 'November 2026' },
  { value: '2026-12', label: 'Desember 2026' },
  { value: '2027-01', label: 'Januari 2027' },
]

const penyelenggaraOptions = [
  { value: '', label: 'Semua Penyelenggara' },
  { value: 'pw', label: 'Pimpinan Wilayah' },
  { value: 'surabaya', label: 'Pimpinan Daerah Surabaya' },
  { value: 'jember', label: 'Pimpinan Daerah Jember' },
  { value: 'kediri', label: 'Pimpinan Daerah Kediri' },
  { value: 'malang', label: 'Pimpinan Daerah Malang' },
  { value: 'sidoarjo', label: 'Pimpinan Daerah Sidoarjo' },
  { value: 'gresik', label: 'Pimpinan Daerah Gresik' },
]

// Status badge component
function StatusBadge({ status }: { status: StatusType }) {
  const configs = {
    akan: { label: 'Akan Datang', bg: 'bg-amber-100', text: 'text-amber-700' },
    sedang: { label: 'Sedang Berlangsung', bg: 'bg-emerald-100', text: 'text-emerald-700' },
    selesai: { label: 'Selesai', bg: 'bg-gray-200', text: 'text-gray-500' },
  }
  const config = configs[status]
  return (
    <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  )
}

// Status icon component
function StatusIcon({ status, isActive }: { status: StatusType; isActive: boolean }) {
  const baseClass = `w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all duration-200 ${
    isActive ? 'border-transparent' : 'border-gray-200 bg-white shadow-sm'
  }`

  const iconClass = `w-5 h-5 ${isActive ? 'fill-white' : 'fill-gray-400'}`

  const colors = {
    akan: '#D97706',
    sedang: '#059669',
    selesai: '#6B7280',
  }

  if (status === 'akan') {
    return (
      <div className={baseClass} style={isActive ? { backgroundColor: colors.akan, boxShadow: '0 4px 12px rgba(217, 119, 6, 0.35)' } : {}}>
        <svg className={iconClass} viewBox="0 0 24 24" style={!isActive ? { fill: colors.akan } : {}}>
          <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/>
        </svg>
      </div>
    )
  }

  if (status === 'sedang') {
    return (
      <div className={baseClass} style={isActive ? { backgroundColor: colors.sedang, boxShadow: '0 4px 12px rgba(5, 150, 105, 0.35)' } : {}}>
        <svg className={iconClass} viewBox="0 0 24 24" style={!isActive ? { fill: colors.sedang } : {}}>
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"/>
        </svg>
      </div>
    )
  }

  // selesai
  return (
    <div className={baseClass} style={isActive ? { backgroundColor: colors.selesai, boxShadow: '0 4px 12px rgba(107, 114, 128, 0.35)' } : {}}>
      <svg className={iconClass} viewBox="0 0 24 24" style={!isActive ? { fill: colors.selesai } : {}}>
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm3.88-11.71L10 14.17l-1.88-1.88a.996.996 0 10-1.41 1.41l2.59 2.59c.39.39 1.02.39 1.41 0L17.3 9.7a.996.996 0 100 1.41c-.39.39-1.03.39-1.42 0z"/>
      </svg>
    </div>
  )
}

// Filter select component
function FilterSelect({ value, onChange, options }: {
  value: string
  onChange: (v: string) => void
  options: { value: string; label: string }[]
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex-1 h-9 px-3 pr-8 rounded-xl border border-green-200 font-rajdhani text-sm font-medium text-gray-700 bg-white appearance-none cursor-pointer outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 shadow-sm"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='%236B7280'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 10px center',
      }}
    >
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>{opt.label}</option>
      ))}
    </select>
  )
}

// Activity card component
function ActivityCard({ kegiatan }: { kegiatan: Kegiatan }) {
  const navigate = useNavigate()

  // Mock data for preview - dalam implementasi real akan dari API
  const mockData = {
    akan: {
      title: 'Konsolidasi ISWARA PW Jawa Timur 2026',
      badge: 'akan' as StatusType,
      Penyelenggara: { type: 'pw', name: 'Pimpinan Wilayah' },
      tanggal: '15 Nov 2026',
      lokasi: 'Surabaya',
      status: 'Pendaftaran',
    },
    sedang: {
      title: 'Pelatihan Kewirausahaan PDA Jember',
      badge: 'sedang' as StatusType,
      Penyelenggara: { type: 'pda', name: 'Pimpinan Daerah Jember' },
      tanggal: '10 - 12 Okt 2026',
      lokasi: 'Jember',
      status: 'Finalisasi Peserta',
    },
    selesai: {
      title: 'Raker ISWARA PW Jawa Timur 2026',
      badge: 'selesai' as StatusType,
      Penyelenggara: { type: 'pw', name: 'Pimpinan Wilayah' },
      tanggal: '5 Okt 2026',
      lokasi: 'Surabaya',
      status: '',
    },
  }

  // Untuk preview, gunakan mock data
  // Dalam implementasi real, data akan dari props kegiatan
  const data = mockData[Object.keys(mockData)[0] as keyof typeof mockData]

  // TODO: Replace with real data from kegiatan prop
  // const data = {
  //   title: kegiatan.header || kegiatan.tema,
  //   tanggal: formatDate(kegiatan.tanggal_mulai),
  //   lokasi: kegiatan.lokasi,
  //   Penyelenggara: getPenyelenggara(kegiatan),
  // }

  return (
    <div
      onClick={() => navigate(`/kegiatan/${kegiatan.id}`)}
      className="bg-white rounded-2xl shadow-card p-4 mb-3 cursor-pointer transition-all duration-200 hover:shadow-card-hover active:scale-[0.99]"
    >
      {/* Header with badge */}
      <div className="flex justify-between items-start mb-2.5">
        <h3 className="text-base font-bold text-primary flex-1 pr-3 leading-snug">
          {data.title}
        </h3>
        <StatusBadge status={data.badge} />
      </div>

      {/* Penyelenggara */}
      <div className="flex items-center gap-1.5 text-sm text-gray-600 mb-3">
        <svg className="w-4 h-4 fill-primary" viewBox="0 0 24 24">
          <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
        </svg>
        <span>
          <span className="font-bold text-primary">{data.Penyelenggara.type === 'pw' ? 'Pimpinan Wilayah' : 'Pimpinan Daerah'}</span>
          {' '}{data.Penyelenggara.name.replace('Pimpinan Wilayah ', '')}
        </span>
      </div>

      {/* Details */}
      <div className="flex flex-wrap items-center gap-3 pt-3 border-t border-dashed border-gray-200">
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <svg className="w-4 h-4 fill-gray-400" viewBox="0 0 24 24">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11z"/>
          </svg>
          {data.tanggal}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <svg className="w-4 h-4 fill-gray-400" viewBox="0 0 24 24">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          {data.lokasi}
        </div>
        {data.status && (
          <>
            <div className="w-px h-4 bg-gray-200" />
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <svg className="w-4 h-4 fill-gray-400" viewBox="0 0 24 24">
                <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
              {data.status}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Empty state for no activities
function EmptyActivities() {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <svg className="w-16 h-16 fill-gray-300 mb-4" viewBox="0 0 24 24">
        <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm2 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
      </svg>
      <p className="text-gray-500 font-rajdhani text-sm">Belum ada kegiatan dalam filter ini</p>
    </div>
  )
}

export default function AdminDashboardPage() {
  const navigate = useNavigate()
  const role = useAuthStore((s) => s.role)
  const isSuperAdmin = role === 'super_admin'

  // State for filters
  const [activeStatus, setActiveStatus] = useState<StatusType>('akan')
  const [filterTanggal, setFilterTanggal] = useState('')
  const [filterPenyelenggara, setFilterPenyelenggara] = useState('')

  // TODO: Integrate with real API
  // const { data, isLoading } = useKegiatanList({
  //   status: mapStatus(activeStatus),
  //   tanggal: filterTanggal || undefined,
  //   Penyelenggara: filterPenyelenggara || undefined,
  // })

  // Mock loading state
  const isLoading = false

  // Mock items for preview
  const items: Kegiatan[] = []

  // Get color for active status label
  const getStatusLabelColor = (status: StatusType) => {
    const colors = { akan: 'text-amber-600', sedang: 'text-emerald-600', selesai: 'text-gray-500' }
    return colors[status]
  }

  return (
    <div className="-mx-4">
      {/* Status Tabs with Circle Icons - ISWARA Style */}
      <div className="bg-white px-4 py-4 border-b border-gray-100">
        <div className="flex justify-center gap-2">
          {statusTabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => setActiveStatus(tab.value)}
              className={`flex flex-col items-center gap-1.5 px-4 py-2 transition-all duration-200 ${
                activeStatus === tab.value ? '' : 'opacity-60'
              }`}
            >
              <StatusIcon status={tab.value} isActive={activeStatus === tab.value} />
              <span
                className={`text-[11px] font-bold leading-tight text-center whitespace-pre-line ${
                  activeStatus === tab.value ? getStatusLabelColor(tab.value) : 'text-gray-500'
                }`}
              >
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Filter Dropdowns */}
      <div className="bg-white px-4 py-3 border-b border-gray-100 flex gap-2.5">
        <FilterSelect
          value={filterTanggal}
          onChange={setFilterTanggal}
          options={tanggalOptions}
        />
        <FilterSelect
          value={filterPenyelenggara}
          onChange={setFilterPenyelenggara}
          options={penyelenggaraOptions}
        />
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {/* Section Label */}
        <p className="text-[11px] font-bold text-gray-400 tracking-wide mb-3 uppercase">
          Penyelenggara
        </p>

        {/* Activity Cards */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-2xl h-32 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyActivities />
        ) : (
          <div>
            {items.map((k) => (
              <ActivityCard key={k.id} kegiatan={k} />
            ))}
          </div>
        )}

        {/* Placeholder cards for preview */}
        <ActivityCard kegiatan={{ id: '1' } as Kegiatan} />
        <ActivityCard kegiatan={{ id: '2' } as Kegiatan} />
        <ActivityCard kegiatan={{ id: '3' } as Kegiatan} />
      </div>

      {/* FAB for Super Admin */}
      {isSuperAdmin && (
        <button
          onClick={() => navigate('/admin/kegiatan/new')}
          className="fixed bottom-20 right-4 w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 text-white rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-all z-40"
          style={{ boxShadow: '0 4px 16px rgba(249, 160, 40, 0.5)' }}
        >
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        </button>
      )}
    </div>
  )
}
